import { useT } from "@mmm/ui";
export type SortDirection = "asc" | "desc";

interface SortableColumnHeaderProps {
  label: string;
  /** True when the table is currently sorted by this column. */
  active: boolean;
  direction: SortDirection;
  onSort: () => void;
  /** Right-align numeric/date columns. */
  align?: "start" | "end";
}

/**
 * A `<th>` whose entire label is a sort control.
 *
 * Shared rather than written per table: getting this right means `aria-sort`
 * on the header cell (not the button), a real `<button>` so it is keyboard
 * reachable, and an indicator that only appears on the active column. Those
 * are easy to get subtly wrong once per table.
 */
export function SortableColumnHeader({ label, active, direction, onSort, align = "start" }: SortableColumnHeaderProps) {
  const t = useT();

  return (
    <th
      scope="col"
      className={`mmm-table-th mmm-table-th--${align}`}
      // The a11y contract for a sortable column lives on the header cell, and
      // only the active column may declare a direction.
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className={`mmm-table-sort ${active ? "is-active" : ""}`.trim()}
        onClick={onSort}
        aria-label={t("inventory.sortBy", { column: label })}
      >
        <span>{label}</span>
        {/* Only the sorted column shows an arrow — a glyph on every header
            reads as decoration and stops signalling anything. */}
        <span aria-hidden="true" className="mmm-table-sort-arrow">
          {active ? (direction === "asc" ? "▲" : "▼") : ""}
        </span>
      </button>
    </th>
  );
}
